"use client";

import { useMemo, useState } from "react";
import { Lock, RefreshCw } from "lucide-react";
import { RequireRole } from "../console-shell";
import { api, commandId, formatDate, formatTime, reasonText } from "../../lib/api";
import { effectiveRoles, useSession } from "../../lib/session";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Banner, Button, ButtonRow, ConfirmPanel, DeskCard, Desk, Fact, Facts, Field, FieldGrid, FormSection, Input, Note, PageHeader, Segmented, SelectField, StatusTag } from ".";
import { ReadState, useRead } from "./record-tools";

type Mode = "ACK" | "CHOICE" | "SHORT_TEXT" | "NUMBER";
type Choice = { key: string; label: string };
type Bucket = { organizationId: string; organizationName?: string; audienceCount: number; answeredCount: number; nilCount: number; pendingCount: number; choiceCounts?: Record<string, number>; numberSum?: string };
type TaskSummary = {
  id: string; publicRef: string; version: number; status: "PUBLISHED" | "CLOSED" | "WITHDRAWN"; frozen: boolean; organization: { id: string; name: string };
  title: string; description?: string; responseMode: Mode; choices?: Choice[]; multipleChoice: boolean; numberUnit?: string; allowNil: boolean; nilLabel?: string;
  publishedAt: string; closesAt: string; withdrawnReason?: string; audienceCount: number; respondedCount: number;
};
type TaskDetail = TaskSummary & { stats: { asOf: string; frozen: boolean; totals: Bucket; byOrganization: Bucket[] } };
type Responder = { personId: string; displayName: string; organization: { id: string; name: string }; state: "ANSWERED" | "NIL" | "PENDING" };
type ResponseDetail = Responder & { kind?: "ANSWER" | "NIL"; content?: { keys?: string[]; text?: string; value?: string }; respondedAt?: string };
type WorkOrg = { id: string; name: string; lifecycle?: string; children: WorkOrg[] };

const MODE_LABEL: Record<Mode, string> = { ACK: "确认知悉", CHOICE: "选择", SHORT_TEXT: "简短填写", NUMBER: "填数字" };
const STATE_LABEL: Record<Responder["state"], string> = { ANSWERED: "已回应", NIL: "无", PENDING: "未回应" };

// 回应中是办；截止即定格是终；撤回是实底不加勾（DESIGN §4 状态四形）。
function taskTag(task: TaskSummary) {
  if (task.status === "WITHDRAWN") return <StatusTag tone="neutral">已撤回</StatusTag>;
  return task.frozen ? <StatusTag tone="success">已截止</StatusTag> : <StatusTag tone="brand">回应中</StatusTag>;
}

/** 能在哪些组织发：持会务协调（ACTIVITY_COORDINATOR）的授权范围；依任职授的基层负责人只有本组织。 */
export function usePublishOrganizations() {
  const tree = useRead<{ items: WorkOrg[] }>("/api/v1/orgs/tree");
  const { whoami } = useSession();
  return useMemo(() => {
    const flatten = (nodes: WorkOrg[]): WorkOrg[] => nodes.flatMap((n) => [n, ...flatten(n.children ?? [])]);
    const nodes = flatten(tree.data?.items ?? []);
    const allowed = new Set<string>();
    for (const grant of effectiveRoles(whoami).filter((g) => g.roleCode === "ACTIVITY_COORDINATOR")) {
      for (const id of grant.scopeOrganizationIds) {
        const root = nodes.find((n) => n.id === id);
        if (!root) continue;
        for (const n of grant.scopeMode === "SUBTREE_DYNAMIC" ? flatten([root]) : [root]) allowed.add(n.id);
      }
    }
    return nodes.filter((n) => allowed.has(n.id) && n.lifecycle !== "ARCHIVED").map((n) => ({ value: n.id, label: n.name }));
  }, [tree.data, whoami]);
}

export function DutyWorkspace() {
  return (
    <RequireRole roleCode="ACTIVITY_COORDINATOR">
      <DutyDesk />
    </RequireRole>
  );
}

function DutyDesk() {
  const tasks = useRead<{ items: TaskSummary[] }>("/api/v1/duty-tasks");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const current = selectedId ?? tasks.data?.items.find((t) => t.status !== "WITHDRAWN")?.id ?? null;
  const detail = useRead<TaskDetail>(current ? `/api/v1/duty-tasks/${current}` : null);
  const reloadAll = () => { void tasks.reload(); void detail.reload(); };

  return (
    <Desk wide>
      <PageHeader
        eyebrow="会务与履职 / 定向任务"
        title="定向任务"
        description="向一个组织及其下级的会员发一项需要回应的事：确认、选择、简短填写或填数字，可开「本期无」。通知是固定模板，说明只在任务里给收件人看；汇总只按组织，不排名，不公开。"
        actions={<ButtonRow>
          <Button variant="ghost" aria-label="刷新定向任务" onClick={reloadAll}><RefreshCw size={16} /></Button>
          {!composing ? <Button onClick={() => setComposing(true)}>发布任务</Button> : null}
        </ButtonRow>}
      />
      {composing ? <PublishForm onCancel={() => setComposing(false)} onPublished={(id) => { setComposing(false); setSelectedId(id); reloadAll(); }} /> : null}

      <div className="grid items-start gap-4 lg:grid-cols-[var(--d-queue)_minmax(0,1fr)]">
        <DeskCard title="任务">
          <ReadState {...tasks} retry={tasks.reload} />
          <ul className="list-none p-0">
            {tasks.data?.items.map((task) => (
              <li key={task.id} className="not-first:border-t">
                <button type="button" onClick={() => setSelectedId(task.id)} className={`flex w-full flex-col gap-1 px-3 py-3 text-left ${current === task.id ? "bg-[var(--brand-soft)]" : "hover:bg-muted"}`}>
                  <span className="flex items-start justify-between gap-3"><span className="text-sm font-semibold">{task.title}</span>{taskTag(task)}</span>
                  <span className="text-xs tabular-nums text-muted-foreground">{task.organization.name} · 已回应 {task.respondedCount} / {task.audienceCount} 人 · {formatDate(task.closesAt)} 截止</span>
                </button>
              </li>
            ))}
          </ul>
          {tasks.data && !tasks.data.items.length ? <p className="px-3 py-6 text-xs text-muted-foreground">还没有定向任务。</p> : null}
        </DeskCard>
        <div>{detail.data ? <TaskView task={detail.data} onChanged={reloadAll} /> : <ReadState {...detail} retry={detail.reload} />}</div>
      </div>
    </Desk>
  );
}

function PublishForm({ onCancel, onPublished }: { onCancel: () => void; onPublished: (id: string) => void }) {
  const organizations = usePublishOrganizations();
  const [organizationId, setOrganizationId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [mode, setMode] = useState<Mode>("ACK");
  const [choices, setChoices] = useState("");
  const [multiple, setMultiple] = useState(false);
  const [unit, setUnit] = useState("");
  const [allowNil, setAllowNil] = useState(false);
  const [nilLabel, setNilLabel] = useState("本期无");
  const [days, setDays] = useState("7");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const choiceList = choices.split("\n").map((x) => x.trim()).filter(Boolean);
  const ready = organizationId && title.trim().length >= 2 && (mode !== "CHOICE" || choiceList.length >= 2);

  const publish = async () => {
    setBusy(true); setError("");
    try {
      const closesAt = new Date(Date.now() + Math.max(1, Math.min(60, Number(days) || 7)) * 86_400_000).toISOString();
      const created = await api<TaskDetail>("/api/v1/duty-tasks", {
        method: "POST",
        body: {
          commandId: commandId(), organizationId, title: title.trim(), ...(description.trim() ? { description: description.trim() } : {}), responseMode: mode,
          ...(mode === "CHOICE" ? { choices: choiceList, multipleChoice: multiple } : {}),
          ...(mode === "NUMBER" && unit.trim() ? { numberUnit: unit.trim() } : {}),
          allowNil, ...(allowNil ? { nilLabel: nilLabel.trim() || "本期无" } : {}), closesAt
        }
      });
      onPublished(created.id);
    } catch (caught) {
      setError(reasonText(caught));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const organizationName = organizations.find((o) => o.value === organizationId)?.label ?? "";
  return (
    <DeskCard title="发布定向任务" description="发布后定义不能改：要改就撤回重发。受众是所选组织及其下级的全部有效会员，发布那一刻冻结。">
      <FormSection step="1" title="发给谁">
        <FieldGrid>
          <Field label="组织" hint="只列出你有会务协调授权的组织；选上级会一并发给下级">
            <SelectField value={organizationId} onChange={setOrganizationId} placeholder="选择组织" options={organizations} />
          </Field>
          <Field label="几天后截止" hint="1—60 天">
            <Input type="number" min={1} max={60} value={days} onChange={(e) => setDays(e.target.value)} />
          </Field>
        </FieldGrid>
      </FormSection>
      <FormSection step="2" title="问什么" description="标题会出现在站内通知里；说明只在任务页给收件人看，不进通知。">
        <FieldGrid>
          <Field label="标题" hint="2—40 字" wide><Input value={title} maxLength={40} onChange={(e) => setTitle(e.target.value)} placeholder="例如：2026 年度会费已缴确认" /></Field>
          <Field label="说明" hint="至多 300 字，选填" wide><Textarea value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} /></Field>
        </FieldGrid>
      </FormSection>
      <FormSection step="3" title="怎么回应">
        <Segmented value={mode} onChange={(v) => setMode(v as Mode)} options={(Object.keys(MODE_LABEL) as Mode[]).map((m) => ({ value: m, label: MODE_LABEL[m] }))} />
        <div className="mt-4 grid gap-4">
          {mode === "CHOICE" ? <>
            <Field label="选项" hint="一行一项，2—8 项，每项至多 20 字"><Textarea value={choices} onChange={(e) => setChoices(e.target.value)} placeholder={"周二晚\n周四晚"} /></Field>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={multiple} onCheckedChange={(v) => setMultiple(v === true)} />可以多选</label>
          </> : null}
          {mode === "NUMBER" ? <Field label="单位" hint="选填，如「小时」「元」"><Input value={unit} maxLength={8} onChange={(e) => setUnit(e.target.value)} /></Field> : null}
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={allowNil} onCheckedChange={(v) => setAllowNil(v === true)} />允许回应「没有 / 不适用」（统计时与「未回应」分开计）</label>
          {allowNil ? <Field label="这一项怎么说" hint="2—12 字"><Input value={nilLabel} maxLength={12} onChange={(e) => setNilLabel(e.target.value)} /></Field> : null}
        </div>
      </FormSection>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {confirming ? (
        <ConfirmPanel
          title={`发布到「${organizationName}」`}
          consequence="将向所选组织及其下级的全部有效会员发一条固定内容的站内通知；发布后不能改，只能提前截止或写明原因撤回。"
          confirmLabel="发布"
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void publish()}
        />
      ) : (
        <ButtonRow>
          <Button variant="ghost" onClick={onCancel}>取消</Button>
          <Button disabled={!ready} onClick={() => setConfirming(true)}>发布…</Button>
        </ButtonRow>
      )}
    </DeskCard>
  );
}

function TaskView({ task, onChanged }: { task: TaskDetail; onChanged: () => void }) {
  const [view, setView] = useState("stats");
  const [closing, setClosing] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (path: string, body: object) => {
    setBusy(true); setError("");
    try { await api(path, { method: "POST", body }); setClosing(false); setWithdrawing(false); onChanged(); }
    catch (caught) { setError(reasonText(caught)); }
    finally { setBusy(false); }
  };
  const { stats } = task;
  const nil = task.nilLabel ?? "本期无";
  return (
    <DeskCard
      title={<span className="flex items-center gap-3">{task.title} {taskTag(task)}</span>}
      description={`${task.organization.name} · ${MODE_LABEL[task.responseMode]} · 统计时点 ${formatTime(stats.asOf)}${stats.frozen ? "（已定格）" : "（回应中，数字会变）"} · ${task.publicRef}`}
      actions={task.status === "PUBLISHED" && !task.frozen ? <ButtonRow>
        <Button variant="outline" size="sm" onClick={() => setClosing(true)}>提前截止</Button>
        <Button variant="ghost" size="sm" onClick={() => setWithdrawing(true)}>撤回</Button>
      </ButtonRow> : task.status !== "WITHDRAWN" ? <Button variant="ghost" size="sm" onClick={() => setWithdrawing(true)}>撤回</Button> : null}
    >
      {closing ? <ConfirmPanel title="提前截止" consequence="截止后汇总即定格，会员不能再回应或修改。" confirmLabel="截止" busy={busy}
        onCancel={() => setClosing(false)} onConfirm={() => void run(`/api/v1/duty-tasks/${task.id}/close`, { commandId: commandId(), expectedVersion: task.version })} /> : null}
      {withdrawing ? <ConfirmPanel title="撤回任务" consequence="撤回后那条站内通知一并撤回，会员那边不再出现这项任务；已有的回应作为历史保留。" confirmLabel="撤回" busy={busy} confirmDisabled={!note.trim()}
        onCancel={() => setWithdrawing(false)} onConfirm={() => void run(`/api/v1/duty-tasks/${task.id}/withdraw`, { commandId: commandId(), expectedVersion: task.version, note: note.trim() })}>
        <input className="w-full border px-3 py-2 text-sm" placeholder="撤回原因（必填）" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} aria-label="撤回原因" />
      </ConfirmPanel> : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {task.withdrawnReason ? <Note>撤回原因：{task.withdrawnReason}</Note> : null}
      {task.description ? <Note>说明：{task.description}</Note> : null}
      <Segmented value={view} onChange={setView} options={[{ value: "stats", label: "组织汇总" }, { value: "people", label: "谁回了谁没回" }]} />
      <div className="mt-4">
        {view === "stats" ? (
          <div className="record-table-scroll">
            <table className="record-table">
              <caption className="sr-only">按组织汇总的回应情况</caption>
              <thead><tr>
                <th>组织</th><th className="text-right">收件人</th><th className="text-right">已回应</th>{task.allowNil ? <th className="text-right">{nil}</th> : null}<th className="text-right">未回应</th>
                {(task.choices ?? []).map((c) => <th key={c.key} className="text-right">{c.label}</th>)}
                {task.responseMode === "NUMBER" ? <th className="text-right">合计{task.numberUnit ? `（${task.numberUnit}）` : ""}</th> : null}
              </tr></thead>
              <tbody className="tabular-nums">
                {[...stats.byOrganization, { ...stats.totals, organizationName: "合计" }].map((row) => (
                  <tr key={row.organizationName} className={row.organizationName === "合计" ? "font-semibold" : undefined}>
                    <td>{row.organizationName}</td><td className="text-right">{row.audienceCount}</td><td className="text-right">{row.answeredCount}</td>
                    {task.allowNil ? <td className="text-right">{row.nilCount}</td> : null}<td className="text-right">{row.pendingCount}</td>
                    {(task.choices ?? []).map((c) => <td key={c.key} className="text-right">{row.choiceCounts?.[c.key] ?? 0}</td>)}
                    {task.responseMode === "NUMBER" ? <td className="text-right">{row.numberSum}</td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
            <Note>「未回应」不当 0，也不当「{nil}」。数字是本人填报的合计，未经核实。只按组织汇总，不对个人排序。</Note>
          </div>
        ) : <People task={task} />}
      </div>
    </DeskCard>
  );
}

function People({ task }: { task: TaskDetail }) {
  const people = useRead<{ items: Responder[] }>(`/api/v1/duty-tasks/${task.id}/responders`);
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <>
      <ReadState {...people} retry={people.reload} />
      {people.data ? (
        <div className="record-table-scroll">
          <table className="record-table">
            <caption className="sr-only">收件人的回应状态（不含内容与联系方式）</caption>
            <thead><tr><th>会员</th><th>组织</th><th>状态</th><th /></tr></thead>
            <tbody>
              {people.data.items.map((row) => (
                <tr key={row.personId}>
                  <td>{row.displayName}</td><td>{row.organization.name}</td><td>{STATE_LABEL[row.state]}</td>
                  <td className="text-right">{row.state !== "PENDING" ? <Button variant="outline" size="sm" onClick={() => setOpenId(openId === row.personId ? null : row.personId)}><Lock size={14} />{openId === row.personId ? "收起" : "查看回应"}</Button> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {openId ? <ResponseCard taskId={task.id} personId={openId} task={task} /> : null}
      <Note>名单只有姓名、组织与状态，不含联系方式；回应内容要逐条打开，每次打开都留存查看记录。不提供导出。</Note>
    </>
  );
}

function ResponseCard({ taskId, personId, task }: { taskId: string; personId: string; task: TaskDetail }) {
  const detail = useRead<ResponseDetail>(`/api/v1/duty-tasks/${taskId}/responders/${personId}`);
  const answer = (d: ResponseDetail) => {
    if (d.kind === "NIL") return task.nilLabel ?? "本期无";
    if (task.responseMode === "ACK") return "已确认";
    if (task.responseMode === "CHOICE") return (d.content?.keys ?? []).map((k) => task.choices?.find((c) => c.key === k)?.label ?? k).join("、");
    if (task.responseMode === "NUMBER") return `${d.content?.value ?? ""}${task.numberUnit ? ` ${task.numberUnit}` : ""}`;
    return d.content?.text ?? "";
  };
  return (
    <div className="mt-4 border-t pt-4">
      <ReadState {...detail} retry={detail.reload} />
      {detail.data ? <>
        <Facts>
          <Fact label="会员">{detail.data.displayName} · {detail.data.organization.name}</Fact>
          <Fact label="回应">{answer(detail.data)}</Fact>
          {detail.data.respondedAt ? <Fact label="时间">{formatTime(detail.data.respondedAt)}</Fact> : null}
        </Facts>
        <Note>本人填报，未经核实。本次查看已留存记录。</Note>
      </> : null}
    </div>
  );
}
